import { useState, useRef, useEffect } from 'react';
import { useDebugStore } from '../store/debugStore.js';

const KIND_LABELS = {
  api: 'API',
  'ws-in': 'WS ←',
  'ws-out': 'WS →',
};

const KIND_COLORS = {
  api: '#002882',
  'ws-in': '#16a34a',
  'ws-out': '#d97757',
};

/** @param {string} ts */
function timeFor(ts) {
  try {
    return new Date(ts).toLocaleTimeString('ru-RU', { hour12: false });
  } catch {
    return ts.slice(11, 19);
  }
}

/** @param {object} data */
function dataText(data) {
  if (!data || typeof data !== 'object') return String(data ?? '');
  // Strip large binary-looking fields.
  const copy = { ...data };
  for (const k of Object.keys(copy)) {
    const v = copy[k];
    if (typeof v === 'string' && v.length > 600) copy[k] = v.slice(0, 300) + '…';
  }
  try {
    return JSON.stringify(copy, null, 1);
  } catch {
    return String(data);
  }
}

/** @param {object} entry */
function LogEntry({ entry }) {
  const [expanded, setExpanded] = useState(false);
  const label = KIND_LABELS[entry.kind] || entry.kind;
  const color = KIND_COLORS[entry.kind] || '#666';
  const text = dataText(entry.data);

  return (
    <div className="border-b border-gray-800/50 py-1.5 font-mono text-xs leading-tight">
      <button
        type="button"
        className="flex items-center gap-2 cursor-default select-none hover:!bg-white/5 w-full text-left"
        onClick={() => setExpanded((e) => !e)}
      >
        <span className="text-gray-600 shrink-0">{timeFor(entry.ts)}</span>
        <span className="shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold" style={{ color, background: color + '22' }}>
          {label}
        </span>
        <span className="truncate text-gray-300">{text.slice(0, 140)}</span>
      </button>
      {expanded && (
        <pre className="mt-1 ml-5 pl-2 border-l-2 overflow-auto max-h-60 text-[11px] leading-relaxed break-all whitespace-pre-wrap text-gray-300" style={{ borderColor: color }}>
          {text}
        </pre>
      )}
    </div>
  );
}

/** @param {object} props */
function DebugPanel({ visible }) {
  const logs = useDebugStore((s) => s.logs);
  const clear = useDebugStore((s) => s.clear);
  const [filter, setFilter] = useState('');
  const bottomRef = useRef(null);

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [logs.length]);

  if (!visible) return null;

  const filtered = filter
    ? logs.filter((e) => dataText(e.data).toLowerCase().includes(filter.toLowerCase()))
    : logs;

  return (
    <div className="fixed bottom-4 right-4 w-[min(90vw,520px)] max-h-[70vh] bg-gray-950 border border-gray-800 rounded-xl shadow-2xl flex flex-col z-50 animate-rise">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-800">
        <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">
          Debug Console · {logs.length} events
        </span>
        <button type="button" className="text-xs text-gray-500 hover:text-white transition-colors px-2 py-1 rounded" onClick={clear}>
          Clear
        </button>
      </div>

      {/* Filter */}
      <div className="px-3 py-2 border-b border-gray-800">
        <input
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by content…"
          className="w-full bg-gray-900 border border-gray-800 rounded px-3 py-1.5 text-xs font-mono text-gray-200 placeholder:text-gray-600 focus:outline-none focus:border-gray-700 transition-colors"
        />
      </div>

      {/* Log list */}
      <div className="flex-1 overflow-y-auto px-1 py-1">
        {filtered.length === 0 && (
          <div className="text-xs text-gray-600 text-center py-8">No events yet</div>
        )}
        {filtered.map((entry, i) => (
          <LogEntry key={i} entry={entry} />
        ))}
        <div ref={bottomRef} />
      </div>
    </div>
  );
}

export default DebugPanel;
